import { FC, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  anchor: HTMLElement;
  stepNumber: number;
  stepCount: number;
  title: string;
  body: string;
  onNext: () => void;
  onSkip: () => void;
}

interface Position {
  top: number;
  left: number;
  caretTop: number;
  flipped: boolean;
}

const BUBBLE_WIDTH = 360;
const GAP = 16;
const MARGIN = 12;

const place = (anchor: HTMLElement, bubbleHeight: number): Position => {
  const rect = anchor.getBoundingClientRect();
  const flipped = rect.right + GAP + BUBBLE_WIDTH + MARGIN > window.innerWidth;
  const left = flipped ? rect.left - GAP - BUBBLE_WIDTH : rect.right + GAP;
  const centred = rect.top + rect.height / 2 - bubbleHeight / 2;
  const top = Math.min(Math.max(centred, MARGIN), window.innerHeight - bubbleHeight - MARGIN);
  const caretTop = Math.min(Math.max(rect.top + rect.height / 2 - top, 24), bubbleHeight - 24);

  return { top, left: Math.max(left, MARGIN), caretTop, flipped };
};

export const BetaTourStepBubble: FC<Props> = ({ anchor, stepNumber, stepCount, title, body, onNext, onSkip }) => {
  const [bubble, setBubble] = useState<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const isLast = stepNumber === stepCount;

  useLayoutEffect(() => {
    if (!bubble) return;

    const reposition = () => setPosition(place(anchor, bubble.offsetHeight));

    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);

    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [anchor, bubble]);

  return createPortal(
    <div
      ref={setBubble}
      role="dialog"
      aria-label={title}
      className="nc_beta-tour__bubble"
      style={{
        width: BUBBLE_WIDTH,
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      <div className="nc_beta-tour__meta">
        <span className="nc_beta-tour__counter">
          Step {stepNumber}/{stepCount}
        </span>
        <span className="nc_beta-tour__dots" aria-hidden="true">
          {Array.from({ length: stepCount }, (_, index) => (
            <span key={index} className={index < stepNumber ? 'nc_beta-tour__dot--on' : undefined} />
          ))}
        </span>
      </div>

      <h2 className="nc_beta-tour__title">{title}</h2>
      <p className="nc_beta-tour__body">{body}</p>

      <div className="nc_beta-tour__actions">
        <button type="button" id="betaTourNextButton" className="nc_beta-tour__next" onClick={onNext}>
          {isLast ? 'Start using it' : 'Next'}
        </button>
        {!isLast && (
          <button type="button" id="betaTourSkipButton" className="nc_beta-tour__skip" onClick={onSkip}>
            Skip
          </button>
        )}
      </div>

      <span
        aria-hidden="true"
        className={`nc_beta-tour__caret ${position?.flipped ? 'nc_beta-tour__caret--right' : ''}`}
        style={{ top: position?.caretTop ?? 0 }}
      />
    </div>,
    document.body,
  );
};

export default BetaTourStepBubble;
